// src/app/shared/utils/field-filters.util.ts
import { FilterRule, FilterOperator } from './field-filters.types';

/**
 * Apply all rules to rows with AND semantics.
 * Pure function — no Angular deps.
 */
export function applyFieldFilters(rows: any[], rules: FilterRule[]): any[] {
  if (!rules || !rules.length) return rows;
  return rows.filter(row => rules.every(rule => evaluateRule(row, rule)));
}

/**
 * Count rules that have meaningful values (for badge display).
 */
export function countActiveRules(rules: FilterRule[]): number {
  return rules.filter(rule => {
    const op = rule.operator;
    if (['is_empty', 'is_not_empty', 'is_true', 'is_false',
         'today', 'this_week', 'this_month'].includes(op)) return true;
    if (op === 'is_one_of' || op === 'is_not_one_of') {
      return Array.isArray(rule.value) && rule.value.length > 0;
    }
    return rule.value !== null && rule.value !== undefined && rule.value !== '';
  }).length;
}

function evaluateRule(row: any, rule: FilterRule): boolean {
  const raw = row[rule.key];
  const v = raw ?? '';
  const op: FilterOperator = rule.operator;

  switch (op) {
    case 'is_empty':     return String(v).trim() === '';
    case 'is_not_empty': return String(v).trim() !== '';

    case 'contains':
      return String(v).toLowerCase().includes(String(rule.value ?? '').toLowerCase());
    case 'not_contains':
      return !String(v).toLowerCase().includes(String(rule.value ?? '').toLowerCase());
    case 'equals':
      return String(v).toLowerCase() === String(rule.value ?? '').toLowerCase();
    case 'not_equals':
      return String(v).toLowerCase() !== String(rule.value ?? '').toLowerCase();
    case 'starts_with':
      return String(v).toLowerCase().startsWith(String(rule.value ?? '').toLowerCase());

    case 'eq':  return parseFloat(String(v)) === parseFloat(String(rule.value));
    case 'neq': return parseFloat(String(v)) !== parseFloat(String(rule.value));
    case 'gt':  return parseFloat(String(v)) >  parseFloat(String(rule.value));
    case 'gte': return parseFloat(String(v)) >= parseFloat(String(rule.value));
    case 'lt':  return parseFloat(String(v)) <  parseFloat(String(rule.value));
    case 'lte': return parseFloat(String(v)) <= parseFloat(String(rule.value));

    case 'between': {
      const num = parseFloat(String(v));
      if (!isNaN(num)) {
        return num >= parseFloat(String(rule.value)) &&
               num <= parseFloat(String(rule.value2));
      }
      // date between
      if (!v) return false;
      const d = new Date(String(v));
      return d >= new Date(String(rule.value)) && d <= new Date(String(rule.value2));
    }

    case 'before': return v ? new Date(String(v)) < new Date(String(rule.value)) : false;
    case 'after':  return v ? new Date(String(v)) > new Date(String(rule.value)) : false;

    case 'today': {
      if (!v) return false;
      const d = new Date(String(v)), t = new Date();
      return d.getFullYear() === t.getFullYear() &&
             d.getMonth() === t.getMonth() &&
             d.getDate() === t.getDate();
    }
    case 'this_week': {
      if (!v) return false;
      const d = new Date(String(v)), t = new Date();
      const start = new Date(t); start.setDate(t.getDate() - t.getDay());
      const end   = new Date(start); end.setDate(start.getDate() + 6);
      return d >= start && d <= end;
    }
    case 'this_month': {
      if (!v) return false;
      const d = new Date(String(v)), t = new Date();
      return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth();
    }

    case 'is_one_of':
      return Array.isArray(rule.value) && rule.value.includes(v);
    case 'is_not_one_of':
      return Array.isArray(rule.value) && !rule.value.includes(v);

    case 'is_true':  return !!v;
    case 'is_false': return !v;

    default: return true;
  }
}
